import { useEffect, useMemo } from "react"

import type { AppCommand } from "@/lib/appCommands"
import {
  useKeyboardShortcutsController,
  type ShortcutAction,
} from "@/contexts/KeyboardShortcutsContext"
import { usePageCommands } from "@/hooks/usePageCommands"

type PageShortcutCommand = Omit<
  AppCommand,
  "action" | "id" | "key" | "label"
> & {
  id?: string
  label?: string
}

export type PageShortcut = ShortcutAction & {
  command?: PageShortcutCommand
  enabled?: boolean
}

function normalizeCommandId(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

export function usePageShortcuts(shortcuts: PageShortcut[], enabled = true) {
  const { registerShortcut, unregisterShortcut } =
    useKeyboardShortcutsController()
  const activeShortcuts = useMemo(
    () => shortcuts.filter((shortcut) => shortcut.enabled ?? true),
    [shortcuts],
  )

  const commands = useMemo(
    () =>
      activeShortcuts
        .filter(
          (shortcut) =>
            shortcut.state?.hiddenInPalette !== true &&
            shortcut.command?.available !== false,
        )
        .map((shortcut) => {
          const commandId =
            shortcut.command?.id ??
            `shortcut-${shortcut.key}-${normalizeCommandId(shortcut.label)}`

          return {
            action: shortcut.action,
            aliases:
              shortcut.command?.aliases ??
              (shortcut.key ? [shortcut.key.toLowerCase()] : []),
            available: shortcut.command?.available ?? true,
            description:
              shortcut.command?.description ??
              (typeof shortcut.state?.description === "string"
                ? shortcut.state.description
                : undefined),
            group: shortcut.command?.group,
            id: commandId,
            key: shortcut.key,
            kind: shortcut.command?.kind,
            keywords: shortcut.command?.keywords ?? [
              shortcut.label.toLowerCase(),
              shortcut.key.toLowerCase(),
            ],
            label: shortcut.command?.label ?? shortcut.label,
            parentId: shortcut.command?.parentId,
            scope: shortcut.command?.scope,
          }
        }),
    [activeShortcuts],
  )

  usePageCommands(commands, enabled)

  useEffect(() => {
    if (!enabled) {
      return
    }

    activeShortcuts.forEach((shortcut) => {
      const commandId =
        shortcut.command?.id ??
        `shortcut-${shortcut.key}-${normalizeCommandId(shortcut.label)}`

      registerShortcut({
        ...shortcut,
        state: {
          ...shortcut.state,
          commandId,
        },
      })
    })

    return () => {
      activeShortcuts.forEach((shortcut) => {
        unregisterShortcut(shortcut.key)
      })
    }
  }, [activeShortcuts, enabled, registerShortcut, unregisterShortcut])
}
